/**
 * SignatureFieldNode — custom TipTap inline node.
 *
 * Renders as a non-editable colored chip in the editor. The merchant places
 * these chips anywhere in the document flow; Puppeteer measures their
 * bounding boxes to extract field positions for the signed PDF.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { signerColor } from '../../multiSigner';

export const FIELD_COLORS: Record<string, string> = {
  SIGNATURE: '#7C3AED',
  INITIALS:  '#6D28D9',
  TEXT:      '#2563EB',
  NAME:      '#1D4ED8',
  EMAIL:     '#0369A1',
  DATE:      '#0891B2',
  CHECKBOX:  '#059669',
};

const DEFAULT_LABELS: Record<string, string> = {
  SIGNATURE: 'Customer Signature',
  INITIALS:  'Initials',
  TEXT:      'Text Field',
  NAME:      'Full Name',
  EMAIL:     'Email Address',
  DATE:      'Date',
  CHECKBOX:  'Checkbox',
};

// ---------------------------------------------------------------------------
// React view component
// ---------------------------------------------------------------------------

function SignatureFieldView({ node, deleteNode }: NodeViewProps) {
  const { fieldType, label, signerPosition, required, showIf } = node.attrs as { fieldType: string; label: string; signerPosition?: number; required?: boolean; showIf?: string | null };
  const optional = required === false;
  // Fields for signers 2–3 take that signer's colour and number; customer fields look as before.
  const forOtherSigner = (signerPosition ?? 1) > 1;
  const color = forOtherSigner ? signerColor(signerPosition) : FIELD_COLORS[fieldType] ?? '#2563EB';

  return (
    <NodeViewWrapper as="span" style={{ display: 'inline' }}>
      <span
        contentEditable={false}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '3px 9px 3px 8px',
          borderRadius: '5px',
          background: `${color}18`,
          border: `1.5px solid ${color}55`,
          color,
          fontSize: '12px',
          fontWeight: 600,
          fontStyle: 'normal',
          letterSpacing: '0.01em',
          cursor: 'default',
          userSelect: 'none',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
        title={`${fieldType} field${forOtherSigner ? ` for signer ${signerPosition}` : ''}${optional ? ' (optional)' : ''} — click × to remove`}
      >
        {forOtherSigner && (
          <span style={{ background: color, color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: '10px', fontWeight: 700 }}>
            {signerPosition}
          </span>
        )}
        <span style={{ opacity: 0.7, fontSize: '11px' }}>
          {fieldType === 'SIGNATURE' ? '✍' :
           fieldType === 'INITIALS'  ? 'AB' :
           fieldType === 'NAME'      ? '👤' :
           fieldType === 'EMAIL'     ? '@' :
           fieldType === 'DATE'      ? '📅' :
           fieldType === 'CHECKBOX'  ? '☑' : 'T'}
        </span>
        {label}
        {optional && <span style={{ fontWeight: 500, fontSize: '10px', opacity: 0.75 }}>(optional)</span>}
        {showIf && <span style={{ fontWeight: 500, fontSize: '10px', opacity: 0.75 }} title="Only shown when its checkbox is ticked">(if ☑)</span>}
        <button
          onClick={(e) => { e.stopPropagation(); deleteNode(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color, padding: '0 0 0 2px', lineHeight: 1,
            fontSize: '14px', opacity: 0.6, display: 'flex', alignItems: 'center',
          }}
          title="Remove field"
          aria-label="Remove field"
        >
          ×
        </button>
      </span>
    </NodeViewWrapper>
  );
}

// ---------------------------------------------------------------------------
// TipTap Node extension
// ---------------------------------------------------------------------------

export const SignatureFieldNode = Node.create({
  name: 'signatureField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      fieldType: { default: 'SIGNATURE' },
      label:     { default: 'Signature' },
      fieldId:   { default: null },
      // 1 = customer; 2–3 = the template's extra signers (multi-signer)
      signerPosition: { default: 1 },
      // false = the signer may leave this field empty
      required: { default: true },
      // fieldId of the checkbox that switches this field on (null = always shown)
      showIf: { default: null },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'field-chip',
        'data-field-type': HTMLAttributes.fieldType,
        'data-field-id':   HTMLAttributes.fieldId,
        'data-signer-position': HTMLAttributes.signerPosition,
        'data-required': HTMLAttributes.required === false ? 'false' : 'true',
        'data-show-if': HTMLAttributes.showIf ?? undefined,
      }),
      HTMLAttributes.label,
    ];
  },

  parseHTML() {
    return [{ tag: 'span[data-field-type]' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SignatureFieldView);
  },
});

// Helper: insert a field at the current cursor position
export function insertSignatureField(
  editor: any,
  fieldType: string,
  label?: string,
  signerPosition = 1,
) {
  const fieldId = crypto.randomUUID();
  editor
    .chain()
    .focus()
    .insertContent({
      type: 'signatureField',
      attrs: { fieldType, label: label ?? DEFAULT_LABELS[fieldType] ?? fieldType, fieldId, signerPosition },
    })
    .run();
}
